import moment from "moment-timezone";
import { AvailabilityAutobookLinkDataApiResponse } from "../../services/api/types/autobookAvailability.type";
import { SchedulerStoreType } from "../../state/scheduler.store";
import { getLinkType } from "../link";
import { last } from "lodash-es";
import {
  setLinkHasExpiredError,
  setUserGrantAsInvalidError
} from "../../utils/errorManagement";
import { calculateRange } from "./autobookLink";
import { TFunction } from "i18next";
import { getRoosterAutobookAvailabilityMeta } from "../../services/api/meeting.api";

export const handleAvailabilityLink = async (
  actions: SchedulerStoreType["actions"],
  autobookAvailabilityData: AvailabilityAutobookLinkDataApiResponse,
  linkType: ReturnType<typeof getLinkType>,
  logoUrl: string,
  t: TFunction,
  requestSessionStartedAt = Date.now()
) => {
  if (autobookAvailabilityData?.docData.client && linkType) {
    const availabilityMeta = await getRoosterAutobookAvailabilityMeta({
      linkId: linkType.linkId
    });

    if (!availabilityMeta?.isSenderGrantValid) {
      setUserGrantAsInvalidError(actions, t);
      return;
    }

    actions.setClientLogo(logoUrl);
    actions.setClient("normal", autobookAvailabilityData.docData.client);
    actions.setHost(
      autobookAvailabilityData?.docData?.user?.name ?? "",
      autobookAvailabilityData?.docData?.user?.email ?? ""
    );
    const isCancelled = autobookAvailabilityData?.docData?.cancelled ?? false;
    const availabilityWindowMins = autobookAvailabilityData?.docData?.end ?? 0;
    const duration = autobookAvailabilityData?.docData?.duration ?? 30;
    const availabilityTimezone =
      autobookAvailabilityData?.docData?.timeZone ?? moment.tz.guess();

    const range = calculateRange(
      autobookAvailabilityData,
      availabilityTimezone,
      availabilityWindowMins
    );

    if (moment(last(range)?.rangeEnd).isBefore(moment())) {
      setLinkHasExpiredError(actions, "availability", t);
      return;
    }

    const interviewers = autobookAvailabilityData?.docData?.interviewers?.map(
      (i) => ({
        name:
          autobookAvailabilityData?.docData?.workerData?.find(
            (w) => w.email === i || w.value === i
          )?.name ?? i,
        value: i,
        email: i
      })
    );

    actions.setMeetingLink({
      id: linkType?.linkId,
      type: "rooster",
      mode: "availability",
      availability: {
        type: "range",
        availabilityWindowMins,
        duration,
        autobookIntervalFreq:
          autobookAvailabilityData?.docData?.slotStartIncrement ?? 30,
        range
      },
      interviewers,
      isDisabled: false,
      showTentativeAsBusy: true,
      scheduledData: {
        form: {},
        docDataRooster: autobookAvailabilityData?.docData ?? {},
        availabilityDocData:
          autobookAvailabilityData?.availabilityDocData ?? {},
        alreadyScheduled: false
      },
      canCancel: autobookAvailabilityData?.docData?.cancel ?? false,
      canReschedule: autobookAvailabilityData?.docData?.reschedule ?? false,
      rescheduleStatus:
        autobookAvailabilityData?.docData?.rescheduleStatus ?? null,
      status: autobookAvailabilityData?.status ?? null
    });

    actions.setProviders(autobookAvailabilityData?.docData?.providers ?? []);

    if (isCancelled) {
      actions.setUiState({
        currentView: "month",
        currentStep: "scheduling_cancelled"
      });
      return;
    }

    actions.setSessionData({
      selectedTimezone:
        autobookAvailabilityData?.availabilityDocData?.clientTimeZone ??
        moment.tz.guess(),
      requestSessionStartedAt,
      isRescheduleFlow: false,
      selectedSlot: null,
      prevSelectedSlot: [],
      computedAvailability: [],
      formData: {}
    });

    if (
      autobookAvailabilityData?.availabilityDocData?.availability?.length > 0
    ) {
      actions.setUiState({
        currentView: "availability-slot",
        currentStep: "availability",
        workDayView: true
      });
      return;
    }

    actions.setUiState({
      currentView: "availability-slot",
      currentStep: "initial",
      workDayView: true
    });
  }
};
